import { useAdmin } from '@front/hook/Api/Admin';
import { useFeatureFlag } from '@front/hook/Api/FeatureFlag';
import { useSourceSystem } from '@front/hook/Api/SourceSystem';
import { usePermissions } from '@front/hook/usePermissions';
import { AdminApiError } from '@front/repository/Api/Admin';
import { Button, Checkbox, Loader, useToast } from '@plaidev/sour-react';
import { createFileRoute } from '@tanstack/react-router';
import { useAccount, useProject } from 'auth-lib-front';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { DomainV2 } from 'troppus-common/domain';

export const Route = createFileRoute('/settings/admin-settings/')({
  component: AdminSettingsPage,
});

type FormValues = {
  csvLlmAnalysisEnabled: boolean;
};

const DEFAULT_VALUES: FormValues = {
  csvLlmAnalysisEnabled: DomainV2.FeatureFlag.DefaultFlags.csvLlmAnalysisEnabled,
};

const DEFAULT_SOURCE_SYSTEM_DISPLAY_NAME = 'デフォルト';

/**
 * @feature 管理者設定画面
 * @capabilities CSV列マッピングAI推定の有効化切替, ソースシステム初期化, テーブル定義の強制削除
 * @behavior チェックボックスでCSV列マッピングのAI推定をON/OFF→「保存」で反映, 「初期化」→「デフォルト」ソースシステムを1件作成, ImportTableDefinition ID入力→「強制削除」→確認後に未完了タスクをFailed化しテーブル定義を論理削除（PIIは物理削除）。社内メンバーのみ閲覧でき、初期化はプロジェクト管理者・強制削除はシステム管理者に限定
 * @airule IMPORTANT: Update this JSDoc when modifying this component's functionality
 */
function AdminSettingsPage() {
  const { account } = useAccount();
  const { isSystemAdmin, isProjectAdmin } = usePermissions();
  // この設定ページは社内メンバー (isEmployee) であればアクセス可能。
  // ただし破壊的操作（テーブル定義の強制削除）は真のシステム管理者 (isSystemAdmin)、
  // 初期化（ソースシステム作成）はプロジェクト管理者 (isProjectAdmin) に限定する。
  const isEmployee = account.isEmployee;
  const { project } = useProject();
  const toast = useToast();

  const { data: featureFlagData, isPending: isFeatureFlagPending } = useFeatureFlag.Get({ projectId: project.id }, { enabled: isEmployee });
  const { data: sourceSystems, isPending: isSourceSystemListPending } = useSourceSystem.List(project.id, {
    enabled: isEmployee,
  });
  const updateMutation = useFeatureFlag.Update();
  const createSourceSystemMutation = useSourceSystem.Create();
  const forceRemoveDefinitionMutation = useAdmin.ForceRemoveImportTableDefinition();
  const [forceRemoveTargetId, setForceRemoveTargetId] = useState('');

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<FormValues>({ defaultValues: DEFAULT_VALUES });

  useEffect(() => {
    if (!isEmployee || isFeatureFlagPending || !featureFlagData) return;
    reset({
      csvLlmAnalysisEnabled: featureFlagData.flags.csvLlmAnalysisEnabled ?? DomainV2.FeatureFlag.DefaultFlags.csvLlmAnalysisEnabled,
    });
  }, [isEmployee, isFeatureFlagPending, featureFlagData, reset]);

  if (!isEmployee) {
    return (
      <div className="rounded-lg border border-default bg-subtle-light p-6 text-center text-sm text-muted-light">
        社内メンバーのみ閲覧可能です
      </div>
    );
  }

  if (isFeatureFlagPending || isSourceSystemListPending) {
    return (
      <div className="flex justify-center py-16">
        <Loader />
      </div>
    );
  }

  const handleInitialize = async () => {
    const alreadyExists = sourceSystems?.some((s) => s.displayName === DEFAULT_SOURCE_SYSTEM_DISPLAY_NAME);
    if (alreadyExists) {
      toast.error(`「${DEFAULT_SOURCE_SYSTEM_DISPLAY_NAME}」という名称のソースシステムは既に存在します`);
      return;
    }

    try {
      await createSourceSystemMutation.mutateAsync({
        projectId: project.id,
        createProps: {
          displayName: DEFAULT_SOURCE_SYSTEM_DISPLAY_NAME,
          description: undefined,
          isActive: true,
        },
      });
      toast.success('初期化が完了しました');
    } catch (err) {
      const message = err instanceof Error ? err.message : '初期化に失敗しました';
      toast.error(message);
    }
  };

  const handleForceRemoveDefinition = async () => {
    const trimmed = forceRemoveTargetId.trim();
    if (!trimmed) {
      toast.error('テーブル定義 ID を入力してください');
      return;
    }
    if (!window.confirm(`本当にテーブル定義 ${trimmed} を強制削除しますか？\n未完了タスクは Failed として強制終了されます。`)) {
      return;
    }
    try {
      const result = await forceRemoveDefinitionMutation.mutateAsync({
        projectId: project.id,
        id: DomainV2.Import.ImportTableDefinition.Id.FromString(trimmed),
      });
      toast.success(`強制削除しました（force-finish: ${result.forceFinishedTaskIds.length} 件）`);
      setForceRemoveTargetId('');
    } catch (err) {
      // Cloud Run 上の execution が止まっていないケースは別途分かりやすく出す
      const isExecutionStillRunning =
        err instanceof AdminApiError &&
        (err.errorCodeId === 'Admin.forceRemoveImportTableDefinition.executionStillRunning' ||
          err.errorCodeId === 'Admin.forceFinishImportTask.executionStillRunning');
      const message = isExecutionStillRunning
        ? 'Cloud Run Job 上の execution がまだ停止していません。停止後に再度実行してください。'
        : err instanceof Error
          ? err.message
          : '強制削除に失敗しました';
      toast.error(message);
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    try {
      await updateMutation.mutateAsync({ projectId: project.id, flags: values });
      reset(values);
      toast.success('保存しました');
    } catch (err) {
      const message = err instanceof Error ? err.message : '保存に失敗しました';
      toast.error(message);
    }
  });

  return (
    <div className="flex w-full flex-col gap-6">
      <h1 className="text-xl font-semibold text-default">管理者設定</h1>
      <form className="flex flex-col gap-4" onSubmit={(e) => void onSubmit(e)}>
        <p className="text-sm font-bold text-default">機能フラグ</p>
        <div className="flex flex-col gap-1">
          <Controller
            control={control}
            name="csvLlmAnalysisEnabled"
            render={({ field }) => (
              <Checkbox
                checked={field.value}
                onCheckedChange={(checked) => field.onChange(!!checked)}
                label="CSV 列マッピングの AI 推定を有効化"
              />
            )}
          />
          <p className="pl-6 text-xs text-muted-light">
            テーブル定義作成時、CSV のサンプルを Vertex AI に送信して列の振り分けを推定します。
          </p>
        </div>
        <div className="flex justify-end">
          <Button type="submit" variant="primary" size="sm" className="w-fit" loading={updateMutation.isPending} disabled={!isDirty}>
            保存
          </Button>
        </div>
      </form>
      {isProjectAdmin && (
        <div className="flex flex-col gap-4">
          <p className="text-sm font-bold text-default">初期化</p>
          <p className="text-xs text-muted-light">
            プロジェクトの初期設定として、ソースシステム「{DEFAULT_SOURCE_SYSTEM_DISPLAY_NAME}」を 1 件作成します。
          </p>
          <div className="flex justify-end">
            <Button
              type="button"
              variant="primary"
              size="sm"
              className="w-fit"
              loading={createSourceSystemMutation.isPending}
              onClick={() => void handleInitialize()}
            >
              初期化
            </Button>
          </div>
        </div>
      )}
      {/* 破壊的操作（PII 物理削除を伴う）のため、真のシステム管理者のみに表示・操作を限定する */}
      {isSystemAdmin && (
        <div className="admin-ui flex flex-col gap-4 rounded-md px-3 py-2">
          <p className="text-sm font-bold text-default">テーブル定義の強制削除</p>
          <p className="text-xs text-muted-light">
            途中失敗で <code>Requested</code> / <code>Started</code> のまま残った ImportTask を Failed として強制終了し、続けて
            ImportTableDefinition を論理削除（PII データは物理削除）します。Cloud Run Job 上の execution が停止していないと 409 を返します。
          </p>
          <div className="flex flex-col gap-2">
            <label className="text-xs text-muted-light" htmlFor="forceRemoveTargetId">
              ImportTableDefinition ID
            </label>
            <input
              id="forceRemoveTargetId"
              type="text"
              className="w-full rounded-md border border-default bg-default px-3 py-2 text-sm text-default"
              value={forceRemoveTargetId}
              onChange={(e) => setForceRemoveTargetId(e.target.value)}
              placeholder="itd_xxxxxxxxxxxx"
            />
          </div>
          <div className="flex justify-end">
            <Button
              type="button"
              variant="negative"
              size="sm"
              className="w-fit"
              loading={forceRemoveDefinitionMutation.isPending}
              disabled={forceRemoveTargetId.trim().length === 0}
              onClick={() => void handleForceRemoveDefinition()}
            >
              強制削除
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
